// Commissions → Orders: one row per commission line (a split order has two),
// filterable by status and rep. Clicking a row opens the order's commission
// detail — eligibility, costs, margins, the split and its history.
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  type CommissionLine,
  LINE_STATUS_LABEL,
  errReason,
  fmtDay,
  lineStatusTone,
  money,
  nameOr,
  ratePct,
  reviewText,
  roleLabel,
  usePlans,
} from "@/lib/compPlans";
import { Drawer, Empty, LineStatusCell, Loading, Notice, INPUT, T_HEAD, T_NUM, T_ROW_CLICK, T_TD, T_TH, T_WRAP } from "./parts";
import { OrderCommissionDetail } from "./OrderCommissionDetail";

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "estimated", label: "Estimated" },
  { value: "eligible", label: "Eligible" },
  { value: "approved_for_payout", label: "Approved for payout" },
  { value: "paid", label: "Paid" },
  { value: "needs_review", label: "Needs review" },
];

export function OrdersTab() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const repId = params.get("rep") ?? "";
  const openOrder = params.get("order");

  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  const plans = usePlans();
  const q = useQuery({
    queryKey: ["commissions", "orders", repId],
    queryFn: () => api<{ rows: CommissionLine[] }>(`/commissions/orders${repId ? `?rep_id=${encodeURIComponent(repId)}` : ""}`),
  });

  const rows = (q.data?.rows ?? []).filter((r) =>
    !status ? true : status === "needs_review" ? !!r.needs_review : r.status === status,
  );
  const openRow = openOrder ? (q.data?.rows ?? []).find((r) => r.order_id === openOrder) : undefined;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[14px] font-medium text-ink">Status</span>
          <select className={`${INPUT} w-60`} value={status} onChange={(e) => setParam("status", e.target.value || null)}>
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[14px] font-medium text-ink">Rep</span>
          <select className={`${INPUT} w-64`} value={repId} onChange={(e) => setParam("rep", e.target.value || null)}>
            <option value="">All reps</option>
            {(plans.data?.reps ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name}
              </option>
            ))}
          </select>
        </label>
        {q.data && (
          <p className="pb-2.5 text-[15px] text-muted">
            {rows.length} {rows.length === 1 ? "line" : "lines"} · {money(rows.reduce((s, r) => s + Number(r.amount ?? 0), 0))}
          </p>
        )}
      </div>

      {q.isError ? (
        <Notice tone="risk">{errReason(q.error)}</Notice>
      ) : q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No commission lines match. Commission appears here once an order has its final invoice.</Empty>
      ) : (
        <div className={T_WRAP}>
          <table className="w-full min-w-[1500px]">
            <thead className={T_HEAD}>
              <tr>
                <th className={T_TH}>Client / opportunity</th>
                <th className={T_TH}>Order</th>
                <th className={T_TH}>Invoice</th>
                <th className={T_TH}>Rep</th>
                <th className={`${T_TH} text-right`}>Revenue</th>
                <th className={`${T_TH} text-right`}>Gross profit</th>
                <th className={T_TH}>Plan · rate</th>
                <th className={`${T_TH} text-right`}>Share</th>
                <th className={`${T_TH} text-right`}>Commission</th>
                <th className={T_TH}>Status</th>
                <th className={T_TH}>Eligible</th>
                <th className={T_TH}>Payout period</th>
                <th className={T_TH}>Paid</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={T_ROW_CLICK} onClick={() => setParam("order", r.order_id)}>
                  <td className={T_TD}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setParam("order", r.order_id);
                      }}
                      className="block text-left font-medium underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
                      aria-label={`Open commission for order ${r.order_number}${r.client_name ? `, ${r.client_name}` : ""}`}
                    >
                      {r.client_name ?? "No client"}
                    </button>
                    <Link
                      to={`/projects/${r.project_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[14px] text-muted underline-offset-2 hover:text-ink hover:underline"
                    >
                      {r.opportunity ?? "Opportunity"}
                    </Link>
                  </td>
                  <td className={`${T_TD} tabular-nums`}>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        setParam("order", r.order_id);
                      }}
                      className="hover:underline"
                    >
                      #{r.order_number}
                    </button>
                  </td>
                  <td className={`${T_TD} tabular-nums`}>{r.invoice_number ? `#${r.invoice_number}` : "—"}</td>
                  <td className={T_TD}>
                    <span className="block">{nameOr(r.rep_name)}</span>
                    <span className="text-[14px] text-muted">{roleLabel(r.rep_role)}</span>
                  </td>
                  <td className={T_NUM}>{money(r.revenue)}</td>
                  <td className={T_NUM}>{money(r.gross_profit)}</td>
                  <td className={T_TD}>
                    {r.plan_name ? (
                      <>
                        <span className="block">
                          {r.plan_name}
                          {r.plan_version ? <span className="text-muted"> v{r.plan_version}</span> : null}
                        </span>
                        <span className="text-[14px] text-muted">{ratePct(r.rate)} of gross profit</span>
                      </>
                    ) : (
                      <span className="text-muted">No plan</span>
                    )}
                  </td>
                  <td className={T_NUM}>{r.share_percent != null ? `${Number(r.share_percent)}%` : "—"}</td>
                  <td className={`${T_NUM} font-semibold`}>{money(r.amount)}</td>
                  <td className={T_TD}>
                    <LineStatusCell
                      status={r.status}
                      label={LINE_STATUS_LABEL[r.status] ?? r.status}
                      tone={lineStatusTone(r.status)}
                      waitingOn={r.waiting_on}
                      reviewText={r.needs_review ? r.reviewText ?? reviewText(r.needs_review) : null}
                    />
                  </td>
                  <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(r.eligible_at)}</td>
                  <td className={`${T_TD} whitespace-nowrap`}>{r.batch_period_start ? fmtDay(r.batch_period_start) : "—"}</td>
                  <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(r.paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openOrder && (
        <Drawer
          title={openRow ? `Order #${openRow.order_number} — ${openRow.client_name ?? "No client"}` : "Order commission"}
          onClose={() => setParam("order", null)}
        >
          <OrderCommissionDetail key={openOrder} orderId={openOrder} />
        </Drawer>
      )}
    </div>
  );
}
